import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight, CheckCircle } from 'lucide-react';
import './DemoPage.css';
import logo from '../../images/logo_white.png';
import { supabase } from '../../../config/supabase';

/* ─── Survey question data from the readiness survey ─── */
const CBM_QUESTIONS = [
  "Are you happy with your organisation's competency structure, or would you like to review it?",
  "Are you happy with the competency domains you currently have; do you feel they are well placed to serve your organisation's performance requirements?",
  "Are you happy with your organisation's role-level competence modelling?",
  "Given your overall performance requirements, what should your enterprise-wide talent map look like?",
  "Do you have functional talent maps for each function in your organisation?",
  "Have you developed role-level talent maps for all the roles in your organisation?",
  "Do all employees in your organisation have personal competence maps?",
  "Do you have a talent measurement tool; are you happy with it?",
  "Do you have a talent evaluation tool; are you happy with it?",
  "Are you happy with the policies and implementation guidelines that direct talent management in your organisation?",
];

const NON_CBM_QUESTIONS = [
  "Are you clear about the competency structure you would like to adopt for your organisation? If you do, what structure would you like to adopt?",
  "Do you have an idea of the competency domains you would like to have in the respective competency categories in your organisation? If you do, which are these?",
  "Would you like to adopt linear or non-linear competency modelling?",
  "Do you have a well-documented business strategy that articulates your organisation's performance requirements and the anticipated outcomes?",
  "Do you have functional performance plans for each department/function? If yes, are they aligned with the enterprise business strategy?",
  "Do all roles in your organisation have updated job descriptions and performance requirements?",
  "Do all employees in your organisation have personal performance plans and scorecards against which they are periodically evaluated?",
  "Do you have a talent measurement tool; are you happy with it?",
  "Do you have a talent evaluation tool; are you happy with it?",
  "Are you happy with the policies and implementation guidelines that direct talent management in your organisation?",
];

const RESPONSE_OPTIONS = ['Yes', 'No', 'Not sure'];

/* ─── Navbar shared across steps ─── */
function DemoNavbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <nav className="demo-navbar">
      <div className="demo-nav-content">
        <div className="demo-logo" onClick={() => (window.location.href = '/')}>
          <img src={logo} alt="TalentScope360" />
        </div>

        <button
          className="demo-hamburger"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        <div className="demo-nav-links demo-desktop-only">
          <a href="/#features" className="demo-nav-link">Features</a>
          <Link to="/pricing" className="demo-nav-link">Pricing</Link>
          <Link to="/documentation" className="demo-nav-link">Documentation</Link>
        </div>

        <div className="demo-nav-buttons demo-desktop-only">
          <a href="/login" className="demo-btn-login">Login</a>
          <Link to="/sign-up" className="demo-btn-getstarted">Get Started</Link>
        </div>
      </div>

      <div className={`demo-mobile-menu ${mobileOpen ? 'open' : ''}`}>
        <a href="/#features" className="demo-mobile-link" onClick={() => setMobileOpen(false)}>Features</a>
        <Link to="/pricing" className="demo-mobile-link" onClick={() => setMobileOpen(false)}>Pricing</Link>
        <Link to="/documentation" className="demo-mobile-link" onClick={() => setMobileOpen(false)}>Documentation</Link>
        <div className="demo-mobile-buttons">
          <a href="/login" className="demo-btn-login">Login</a>
          <Link to="/sign-up" className="demo-btn-getstarted">Get Started</Link>
        </div>
      </div>
    </nav>
  );
}

/* ─── Step 1: Intro / profile form ─── */
function IntroStep({ formData, setFormData, onDashboard, demoLoading, onSurvey }) {
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!formData.fullName.trim())    e.fullName    = 'Please enter your name';
    if (!formData.jobTitle.trim())    e.jobTitle    = 'Please enter your role';
    if (!formData.companyName.trim()) e.companyName = 'Please enter your organisation';
    if (!formData.companyEmail.trim()) e.companyEmail = 'Please enter your email';
    if (!formData.companySize)        e.companySize = 'Please select a size';
    if (!formData.usesCBM)            e.usesCBM     = 'Please select an option';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const field = (key, label, type = 'text', placeholder = '') => (
    <div className="demo-form-group">
      <label className="demo-label">{label} <span className="demo-required">*</span></label>
      <input
        type={type}
        className={`demo-input ${errors[key] ? 'demo-input-error' : ''}`}
        placeholder={placeholder}
        value={formData[key]}
        onChange={e => setFormData({ ...formData, [key]: e.target.value })}
      />
      {errors[key] && <span className="demo-error-msg">{errors[key]}</span>}
    </div>
  );

  return (
    <div className="demo-intro-wrapper">
      <div className="demo-intro-header">
        <h1>TalentScope360 in Action</h1>
        <p>
          Complete your profile below to get started — then jump straight into the platform
          or take a quick readiness survey first.
        </p>
      </div>

      <div className="demo-card demo-intro-card">
        <h2 className="demo-card-title demo-card-title--green">Let's Get You Started</h2>

        <div className="demo-form-grid">
          {field('fullName',     'Full Name',          'text',  'Jane Mwangi')}
          {field('companyEmail', 'Work Email',         'email', 'jane@talentscope360.com')}
          {field('companyName',  'Organisation Name',  'text',  'TalentScope360 Ltd')}
          {field('jobTitle',     'Job Title / Role',   'text',  'Head of HR')}

          <div className="demo-form-group">
            <label className="demo-label">Organisation Size <span className="demo-required">*</span></label>
            <select
              className={`demo-select ${errors.companySize ? 'demo-input-error' : ''}`}
              value={formData.companySize}
              onChange={e => setFormData({ ...formData, companySize: e.target.value })}
            >
              <option value="">Select size…</option>
              <option value="1-50">1–50 employees</option>
              <option value="51-200">51–200 employees</option>
              <option value="201-500">201–500 employees</option>
              <option value="501-1000">501–1,000 employees</option>
              <option value="1000+">1,000+ employees</option>
            </select>
            {errors.companySize && <span className="demo-error-msg">{errors.companySize}</span>}
          </div>

          <div className="demo-form-group">
            <label className="demo-label">Number of Core Departments</label>
            <input
              type="number"
              min="1"
              className="demo-input"
              placeholder="e.g. 8"
              value={formData.departments}
              onChange={e => setFormData({ ...formData, departments: e.target.value })}
            />
          </div>
        </div>

        {/* CBM question */}
        <div className={`demo-cbm-group ${errors.usesCBM ? 'demo-cbm-error' : ''}`}>
          <label className="demo-label">
            Are you currently using competency-based talent management?{' '}
            <span className="demo-required">*</span>
          </label>
          <div className="demo-radio-row">
            {['yes', 'no'].map(opt => (
              <label key={opt} className={`demo-radio-card ${formData.usesCBM === opt ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="usesCBM"
                  value={opt}
                  checked={formData.usesCBM === opt}
                  onChange={e => setFormData({ ...formData, usesCBM: e.target.value })}
                />
                <span className="demo-radio-label">
                  {opt === 'yes' ? 'Yes, we already use it' : 'No, we are new to it'}
                </span>
              </label>
            ))}
          </div>
          {errors.usesCBM && <span className="demo-error-msg">{errors.usesCBM}</span>}
        </div>

        {/* CTAs */}
        <div className="demo-intro-actions">
          <button
            className="demo-btn-primary"
            onClick={() => { if (validate()) onDashboard(); }}
            disabled={demoLoading}
          >
            {demoLoading ? 'Loading Demo…' : <><span>Proceed to Demo</span> <ArrowRight size={17} /></>}
          </button>
          <button
            className="demo-btn-secondary"
            onClick={() => { if (validate()) onSurvey(); }}
          >
            Take Readiness Survey
          </button>
        </div>

        <p className="demo-action-hint">
          <strong>Proceed to Demo</strong> — explore the platform right away.&nbsp;
          <strong>Take Readiness Survey</strong> — answer 10 quick questions to gauge your
          organisation's readiness before exploring.
        </p>
      </div>
    </div>
  );
}

/* ─── Step 2: Survey ─── */
function SurveyStep({ formData, answers, setAnswers, remarks, setRemarks, onComplete, onBack }) {
  const questions = formData.usesCBM === 'yes' ? CBM_QUESTIONS : NON_CBM_QUESTIONS;
  const surveyTitle = 'Readiness Survey';

  const answeredCount = Object.keys(answers).length;
  const progress = Math.round((answeredCount / questions.length) * 100);

  const handleAnswer = (idx, val) => setAnswers(prev => ({ ...prev, [idx]: val }));
  const handleRemark = (idx, val) => setRemarks(prev => ({ ...prev, [idx]: val }));

  return (
    <div className="demo-survey-wrapper">
      <div className="demo-survey-header">
        <h1>{surveyTitle}</h1>
        <p>
          {formData.usesCBM === 'yes'
            ? 'Review your existing setup to identify what to carry forward and what to strengthen.'
            : 'Evaluate your starting point to shape the right implementation approach for your organisation.'}
        </p>
      </div>

      {/* Progress bar */}
      <div className="demo-progress-wrap">
        <div className="demo-progress-bar">
          <div className="demo-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="demo-progress-label">{answeredCount} of {questions.length} answered</span>
      </div>

      {/* Question cards */}
      <div className="demo-survey-card">
        <div className="demo-survey-table-header">
          <span className="demo-col-question">Critical Decision Area</span>
        </div>

        {questions.map((q, idx) => (
          <div key={idx} className={`demo-question-row ${answers[idx] ? 'demo-question-answered' : ''}`}>
            <div className="demo-question-body">
              <span className="demo-question-num">{idx + 1}.</span>
              <span className="demo-question-text">{q}</span>
            </div>

            <div className="demo-response-options">
              {RESPONSE_OPTIONS.map(opt => (
                <label key={opt} className={`demo-response-label ${answers[idx] === opt ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name={`q-${idx}`}
                    value={opt}
                    checked={answers[idx] === opt}
                    onChange={() => handleAnswer(idx, opt)}
                  />
                  <span className="demo-response-dot" />
                  <span className="demo-response-text">{opt}</span>
                </label>
              ))}
            </div>

            <div className="demo-remarks-wrap">
              <input
                type="text"
                className="demo-remarks-input"
                placeholder="Remarks (optional)"
                value={remarks[idx] || ''}
                onChange={e => handleRemark(idx, e.target.value)}
              />
            </div>
          </div>
        ))}

        <div className="demo-survey-actions">
          <button className="demo-btn-ghost" onClick={onBack}>
            ← Back
          </button>
          <button
            className="demo-btn-primary"
            onClick={onComplete}
            disabled={answeredCount === 0}
          >
            Submit Survey <ArrowRight size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Step 3: Complete / Results ─── */
function CompleteStep({ formData, answers, questions, onExplore, exploreLoading }) {
  const yesCount = Object.values(answers).filter(v => v === 'Yes').length;
  const total = Object.keys(answers).length;

  const readiness =
    total === 0 ? 0 : Math.round((yesCount / questions.length) * 100);

  let readinessLabel, readinessColor, readinessBg, readinessNote, nextStepHint;
  if (readiness >= 70) {
    readinessLabel = 'High Readiness';
    readinessColor = '#006b54';
    readinessBg    = 'rgba(0,107,84,0.07)';
    readinessNote  =
      'Your organisation has a strong foundation. TalentScope360 can help you optimise and scale your existing approach.';
    nextStepHint   = 'Explore the platform and see how TalentScope360 fits into your current setup.';
  } else if (readiness >= 40) {
    readinessLabel = 'Moderate Readiness';
    readinessColor = '#c87000';
    readinessBg    = 'rgba(200,112,0,0.07)';
    readinessNote  =
      'You have some key elements in place. TalentScope360 will help you fill the gaps and build a more consistent framework.';
    nextStepHint   = 'Start exploring the platform — or book a call so we can walk you through a tailored plan.';
  } else {
    readinessLabel = 'Building Readiness';
    readinessColor = '#b03020';
    readinessBg    = 'rgba(176,48,32,0.07)';
    readinessNote  =
      'This is a great time to start. TalentScope360 is built to support organisations developing their competency management capability from the ground up.';
    nextStepHint   = 'We recommend a consultation call so we can guide you through the best starting point.';
  }

  return (
    <div className="demo-complete-wrapper">
      <div className="demo-complete-hero">
        <div className="demo-complete-icon-wrap">
          <CheckCircle size={36} strokeWidth={2.2} />
        </div>
        <h1>Readiness Summary</h1>
        <p className="demo-complete-intro">
          Thank you, <strong>{formData.fullName}</strong>. Here's how <strong>{formData.companyName}</strong> is
          positioned for competency-based talent management.
        </p>
      </div>

      <div className="demo-results-card">
        {/* Score section */}
        <div className="demo-score-section" style={{ background: readinessBg }}>
          <svg className="demo-score-ring" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="9" />
            <circle
              cx="60" cy="60" r="50"
              fill="none"
              stroke={readinessColor}
              strokeWidth="9"
              strokeDasharray={`${readiness * 3.14} 314`}
              strokeLinecap="round"
              transform="rotate(-90 60 60)"
            />
            <text x="60" y="55" textAnchor="middle" className="demo-score-pct" fill={readinessColor}>
              {readiness}%
            </text>
            <text x="60" y="71" textAnchor="middle" className="demo-score-sub" fill="#6a8a80">
              Readiness
            </text>
          </svg>
          <div className="demo-readiness-label" style={{ color: readinessColor }}>
            {readinessLabel}
          </div>
        </div>

        {/* Interpretation */}
        <div className="demo-results-body">
          <p className="demo-readiness-note">{readinessNote}</p>
          <p className="demo-next-step-hint">{nextStepHint}</p>
        </div>
      </div>

      <div className="demo-complete-actions">
        <button className="demo-btn-primary" onClick={onExplore} disabled={exploreLoading}>
          {exploreLoading ? 'Loading Demo…' : <><span>Explore the Platform</span> <ArrowRight size={17} /></>}
        </button>
        <Link to="/contact-us" className="demo-btn-secondary">
          Book a Consultation Call
        </Link>
      </div>
    </div>
  );
}

/* ─── Demo login helper ─── */
async function loginAsDemo() {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const demoEmail   = process.env.REACT_APP_DEMO_EMAIL;
  const demoPass    = process.env.REACT_APP_DEMO_PASSWORD;

  if (!demoEmail || !demoPass) {
    throw new Error('Demo credentials are not configured.');
  }

  const res = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': supabaseKey,
    },
    body: JSON.stringify({ email: demoEmail, password: demoPass }),
  });

  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error_description || err.message || 'Demo login failed.');
  }

  const session = await res.json();

  // Store session the same way the rest of the app does
  sessionStorage.setItem('supabase-auth-token', JSON.stringify({
    access_token:  session.access_token,
    refresh_token: session.refresh_token,
    expires_at:    Math.floor(Date.now() / 1000) + session.expires_in,
    user:          session.user,
  }));

  return session;
}

/* ─── Main DemoPage orchestrator ─── */
const DemoPage = () => {
  useNavigate();
  const [step, setStep] = useState('intro'); // 'intro' | 'survey' | 'complete'
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError,   setDemoError]   = useState('');
  const [formData, setFormData] = useState({
    fullName: '', jobTitle: '', companyName: '',
    companyEmail: '', companySize: '', departments: '', usesCBM: '',
  });
  const [answers, setAnswers] = useState({});
  const [remarks, setRemarks] = useState({});

  const handleProceedToDemo = async () => {
    setDemoError('');
    setDemoLoading(true);
    // Save visitor profile (skipped survey) non-blocking
    supabase.from('survey_responses').insert([{
      full_name:    formData.fullName,
      job_title:    formData.jobTitle,
      company_name: formData.companyName,
      company_email: formData.companyEmail,
      company_size: formData.companySize,
      departments:  formData.departments || null,
      uses_cbm:     formData.usesCBM,
      answers:      {},
      remarks:      {},
      source:       'direct',
    }]).then(({ error }) => { if (error) console.error('Survey save error:', error); });

    try {
      await loginAsDemo();
      // Full-page redirect so AuthContext re-initialises and picks up the new session.
      window.location.href = '/dashboard';
    } catch (err) {
      setDemoError(err.message);
      setDemoLoading(false);
    }
  };

  const handleSurveyComplete = async () => {
    const questionSet = formData.usesCBM === 'yes' ? CBM_QUESTIONS : NON_CBM_QUESTIONS;
    const yesCount = Object.values(answers).filter(v => v === 'Yes').length;
    const score = questionSet.length > 0
      ? Math.round((yesCount / questionSet.length) * 100)
      : 0;
    const label = score >= 70 ? 'High Readiness'
      : score >= 40 ? 'Moderate Readiness'
      : 'Building Readiness';

    const { error } = await supabase.from('survey_responses').insert([{
      full_name:       formData.fullName,
      job_title:       formData.jobTitle,
      company_name:    formData.companyName,
      company_email:   formData.companyEmail,
      company_size:    formData.companySize,
      departments:     formData.departments || null,
      uses_cbm:        formData.usesCBM,
      answers:         answers,
      remarks:         remarks,
      readiness_score: score,
      readiness_label: label,
      source:          'survey',
    }]);
    if (error) console.error('Survey save error:', error);
    setStep('complete');
  };

  const handleExplore = async () => {
    setDemoError('');
    setDemoLoading(true);
    try {
      await loginAsDemo();
      window.location.href = '/dashboard';
    } catch (err) {
      setDemoError(err.message);
      setDemoLoading(false);
    }
  };

  const questions = formData.usesCBM === 'yes' ? CBM_QUESTIONS : NON_CBM_QUESTIONS;
  const currentYear = new Date().getFullYear();

  return (
    <div className="demo-page">
      <DemoNavbar />

      <main className="demo-main">
        {step === 'intro' && (
          <>
            {demoError && (
              <div style={{
                maxWidth: 520, margin: '16px auto 0',
                background: '#fee2e2', color: '#dc2626',
                borderRadius: 8, padding: '12px 16px', fontSize: 14
              }}>
                {demoError}
              </div>
            )}
            <IntroStep
              formData={formData}
              setFormData={setFormData}
              onDashboard={handleProceedToDemo}
              demoLoading={demoLoading}
              onSurvey={() => setStep('survey')}
            />
          </>
        )}

        {step === 'survey' && (
          <SurveyStep
            formData={formData}
            answers={answers}
            setAnswers={setAnswers}
            remarks={remarks}
            setRemarks={setRemarks}
            onComplete={handleSurveyComplete}
            onBack={() => setStep('intro')}
          />
        )}

        {step === 'complete' && (
          <CompleteStep
            formData={formData}
            answers={answers}
            questions={questions}
            onExplore={handleExplore}
            exploreLoading={demoLoading}
          />
        )}
      </main>

      <div className="demo-footer-bottom">
        <p>© {currentYear} TalentScope360 | All rights reserved.</p>
      </div>
    </div>
  );
};

export default DemoPage;
